'use client';

import { useReducer, useEffect, useCallback } from 'react';
import { LocalStorage } from '@/src/utils/general/local-storage';
import type { CardId } from '../types';

// LocalStorage keys per DASHBOARD_ARCHITECTURE.md
const VISIBILITY_KEY = 'dashboard-control-visibility';
const ORDER_KEY = 'dashboard-controls';

/**
 * VisibilityState - Maps CardId to visibility boolean
 *
 * Replaces 13+ individual useState calls in dashboard-overview.tsx
 * with a single useReducer for better performance and maintainability.
 */
export type VisibilityState = {
  showPerformanceChart: boolean;
  showMetricCards: boolean;
  showTrafficSource: boolean;
  showCategories: boolean;
  showGeoMap: boolean;
  showDevices: boolean;
  showOffers: boolean;
  showLandingPages: boolean;
  showSuspicious: boolean;
  showMediaBuyers: boolean;
  showCampaignList: boolean;
  showProfitabilityCards: boolean;
  showRecommendations: boolean;
  isCompactMetrics: boolean;
};

// Map internal state keys to CardId for external consumers
const visibilityKeyToCardId: Record<keyof Omit<VisibilityState, 'isCompactMetrics'>, CardId | null> = {
  showPerformanceChart: 'hourly',
  showMetricCards: 'metrics',
  showTrafficSource: 'traffic-source',
  showCategories: 'categories',
  showGeoMap: 'geo',
  showDevices: 'devices',
  showOffers: 'offers',
  showLandingPages: 'landing-pages',
  showSuspicious: 'suspicious',
  showMediaBuyers: 'mediabuyers',
  showCampaignList: 'campaign-list',
  showProfitabilityCards: 'profitability',
  showRecommendations: 'recommendations',
};

// Default initial order of cards
const defaultOrder: CardId[] = [
  'metrics',
  'hourly',
  'campaigns',
  'traffic-source',
  'categories',
  'offers',
  'devices',
  'geo',
  'landing-pages',
  'suspicious',
  'mediabuyers',
  'profitability',
  'campaign-list',
  'recommendations',
];

// Default visibility state
const defaultVisibility: VisibilityState = {
  showPerformanceChart: true,
  showMetricCards: true,
  showTrafficSource: true,
  showCategories: true,
  showGeoMap: true,
  showDevices: true,
  showOffers: true,
  showLandingPages: true,
  showSuspicious: true,
  showMediaBuyers: true,
  showCampaignList: true,
  showProfitabilityCards: true,
  showRecommendations: true,
  isCompactMetrics: false,
};

// Action types for the reducer
type VisibilityAction =
  | { type: 'TOGGLE'; key: keyof VisibilityState }
  | { type: 'SET'; key: keyof VisibilityState; value: boolean }
  | { type: 'SET_ALL'; visible: boolean }
  | { type: 'RESET' }
  | { type: 'RESTORE'; state: Partial<VisibilityState> };

/**
 * Reducer for visibility state
 */
function visibilityReducer(state: VisibilityState, action: VisibilityAction): VisibilityState {
  switch (action.type) {
    case 'TOGGLE':
      return { ...state, [action.key]: !state[action.key] };

    case 'SET':
      return { ...state, [action.key]: action.value };

    case 'SET_ALL':
      return {
        showPerformanceChart: action.visible,
        showMetricCards: action.visible,
        showTrafficSource: action.visible,
        showCategories: action.visible,
        showGeoMap: action.visible,
        showDevices: action.visible,
        showOffers: action.visible,
        showLandingPages: action.visible,
        showSuspicious: action.visible,
        showMediaBuyers: action.visible,
        showCampaignList: action.visible,
        showProfitabilityCards: action.visible,
        showRecommendations: action.visible,
        // Don't change compact mode with bulk visibility
        isCompactMetrics: action.visible ? state.isCompactMetrics : false,
      };

    case 'RESET':
      return defaultVisibility;

    case 'RESTORE':
      return { ...defaultVisibility, ...action.state };

    default:
      return state;
  }
}

/**
 * Initialize visibility from localStorage or use defaults
 */
function getInitialVisibility(): VisibilityState {
  const stored = LocalStorage.get(VISIBILITY_KEY);
  if (stored && typeof stored === 'object') {
    return { ...defaultVisibility, ...stored };
  }
  return defaultVisibility;
}

/**
 * Initialize card order from localStorage or use defaults
 */
function getInitialOrder(): CardId[] {
  const stored = LocalStorage.get(ORDER_KEY);
  if (stored && Array.isArray(stored)) {
    return stored as CardId[];
  }
  return defaultOrder;
}

/**
 * useDashboardVisibility - Manages card visibility and ordering
 *
 * Extracted from dashboard-overview.tsx to enable shared state across
 * dashboard sub-routes via context.
 *
 * Features:
 * - Consolidates 13+ useState calls into single useReducer
 * - Persists visibility and order to localStorage
 * - Provides toggle, set, bulk, and reset operations
 */
export function useDashboardVisibility() {
  const [visibility, dispatch] = useReducer(
    visibilityReducer,
    undefined,
    getInitialVisibility
  );

  // Separate state for card ordering (not visibility)
  const [controlOrder, setControlOrderState] = useReducer(
    (state: CardId[], action: { type: 'SET'; order: CardId[] } | { type: 'RESET' }) => {
      switch (action.type) {
        case 'SET':
          return action.order;
        case 'RESET':
          return defaultOrder;
        default:
          return state;
      }
    },
    undefined,
    getInitialOrder
  );

  // Toggle a specific visibility setting
  const toggle = useCallback((key: keyof VisibilityState) => {
    dispatch({ type: 'TOGGLE', key });
  }, []);

  // Set a specific visibility setting
  const set = useCallback((key: keyof VisibilityState, value: boolean) => {
    dispatch({ type: 'SET', key, value });
  }, []);

  // Set all visibility settings at once
  const setAllVisible = useCallback((visible: boolean) => {
    dispatch({ type: 'SET_ALL', visible });
  }, []);

  // Reset to defaults
  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    setControlOrderState({ type: 'RESET' });
  }, []);

  // Update card order
  const setControlOrder = useCallback((order: CardId[]) => {
    setControlOrderState({ type: 'SET', order });
  }, []);

  // Convert visibility state to CardId-based record for components that need it
  const cardVisibility = Object.entries(visibilityKeyToCardId).reduce((acc, [key, cardId]) => {
    if (cardId) {
      acc[cardId] = visibility[key as keyof VisibilityState] as boolean;
    }
    return acc;
  }, {} as Record<CardId, boolean>);

  // Persist visibility to localStorage whenever it changes
  useEffect(() => {
    LocalStorage.set(VISIBILITY_KEY, visibility);
  }, [visibility]);

  // Persist order to localStorage whenever it changes
  useEffect(() => {
    LocalStorage.set(ORDER_KEY, controlOrder);
  }, [controlOrder]);

  return {
    // Raw visibility state for components that need specific keys
    visibility,
    // CardId-based visibility for overview content
    cardVisibility,
    // Card ordering
    controlOrder,
    setControlOrder,
    // Actions
    toggle,
    set,
    setAllVisible,
    reset,
    // Convenience getters for common visibility checks
    showPerformanceChart: visibility.showPerformanceChart,
    showMetricCards: visibility.showMetricCards,
    showTrafficSource: visibility.showTrafficSource,
    showCategories: visibility.showCategories,
    showGeoMap: visibility.showGeoMap,
    showDevices: visibility.showDevices,
    showOffers: visibility.showOffers,
    showLandingPages: visibility.showLandingPages,
    showSuspicious: visibility.showSuspicious,
    showMediaBuyers: visibility.showMediaBuyers,
    showCampaignList: visibility.showCampaignList,
    showProfitabilityCards: visibility.showProfitabilityCards,
    showRecommendations: visibility.showRecommendations,
    isCompactMetrics: visibility.isCompactMetrics,
    // Setters for backward compatibility with existing components
    setShowPerformanceChart: (v: boolean) => set('showPerformanceChart', v),
    setShowMetricCards: (v: boolean) => set('showMetricCards', v),
    setShowTrafficSource: (v: boolean) => set('showTrafficSource', v),
    setShowCategories: (v: boolean) => set('showCategories', v),
    setShowGeoMap: (v: boolean) => set('showGeoMap', v),
    setShowDevices: (v: boolean) => set('showDevices', v),
    setShowOffers: (v: boolean) => set('showOffers', v),
    setShowLandingPages: (v: boolean) => set('showLandingPages', v),
    setShowSuspicious: (v: boolean) => set('showSuspicious', v),
    setShowMediaBuyers: (v: boolean) => set('showMediaBuyers', v),
    setShowCampaignList: (v: boolean) => set('showCampaignList', v),
    setShowProfitabilityCards: (v: boolean) => set('showProfitabilityCards', v),
    setShowRecommendations: (v: boolean) => set('showRecommendations', v),
    setIsCompactMetrics: (v: boolean) => set('isCompactMetrics', v),
  };
}

export type UseDashboardVisibilityReturn = ReturnType<typeof useDashboardVisibility>;
